import { isSodimacStandalone } from '@digital-retail/store-manager';
import { cx } from 'cvb';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { constants } from '../../config/constants';
import { withApplicationContext } from '../../utils/ApplicationContext';
import { ALLOWED_CONFIGS_FOR_UNIT_PRICES } from '../../utils/PriceHelpers';
import { withUser } from '../../utils/UserContext';
import CesPrices, { CesBadge } from '../CesPrices/CesPrices';
import { ProPrice } from '../ProPrice/ProPrice';
import { Price } from './Price/Price';
import { pricesStyles } from './Prices.style';

const WholesaleBadge = dynamic(() => import('../WholesaleBadge/WholesaleBadge'), {
  ssr: false,
});

const Prices = ({
  priceList,
  productId,
  isTabApplicable,
  page,
  layout,
  isPumAvailable,
  pumQuantity,
  pumUnit,
  variant,
  labels,
  isConnectProduct,
  operators,
  selectedOperatorId,
  appCtx,
  discountBadge,
  priceCount,
  product,
  userSegments,
  isUserLoggedIn,
}) => {
  const { store, regionCode = 'cl' } = appCtx;
  const isNewPriceApplicable = appCtx?.siteConfig?.toggles?.isNewPriceApplicable ?? false;
  const isSodimacSA = isSodimacStandalone(store);
  const showUnitPrice = !!ALLOWED_CONFIGS_FOR_UNIT_PRICES.includes(variant);
  const cmrPrice = product?.prices?.find((price) => price.type === constants.PRICE_TYPE.CMR);

  const cesSegment = appCtx?.siteConfig?.configurations?.CES_SEGMENT ?? {};
  const userCESSegment = cesSegment[userSegments?.find((segment) => cesSegment[segment.name])?.name];

  const getUnitPrice = (unitPrice, symbol) => {
    if (!unitPrice || !showUnitPrice) {
      return '';
    }
    const { price, unit } = unitPrice;
    return price.map((p) => `${symbol} ${p} ${unit}`).join(' - ');
  };

  const getPrice = (price, symbol) => {
    return price.map((p) => `${symbol} ${p}`).join(' - ');
  };

  const priceType = {
    PDP: 'pdp-prices',
    POD: 'pod-prices',
    PLP: 'pod-prices',
    CART: 'cart-prices',
    SLP: 'pod-prices',
  };

  const cesPrice =
    userCESSegment !== undefined
      ? product?.promotions?.find((promotion) => promotion.campaignName === userCESSegment)
      : null;

  const proPrice =
    product?.promotions?.find(
      (promotion) =>
        promotion.campaignName === constants.PRICE_TYPE.PRO || promotion.campaignName === constants.PRICE_TYPE.BULKPRICE
    ) || null;

  const filteredPrices = product?.prices?.filter((price) => price.type !== constants.PRICE_TYPE.CMR) || [];

  const lowestPrice = filteredPrices?.length
    ? filteredPrices.reduce((min, price) => (parseFloat(price.price[0]) > parseFloat(min.price[0]) ? min : price))
    : null;
  const cesPriceIsLower = parseFloat(cesPrice?.prices[0]?.price[0]) < parseFloat(lowestPrice?.price[0]);
  const proPriceIsLower = parseFloat(proPrice?.prices[0]?.price[0]) < parseFloat(lowestPrice?.price[0]);
  const showCesPrice =
    cesPrice && isUserLoggedIn && (isSodimacSA || store === constants.STORES.sodimac) && cesPriceIsLower;
  const isWholesaleEnabled = appCtx?.siteConfig?.toggles?.isWholesaleEnabled ?? false;

  return (
    <div
      className={cx('prices', isTabApplicable && 'price-with-tab', `prices-${layout}`)}
      id={`testId-pod-prices-${productId}`}
    >
      {showCesPrice && !cmrPrice ? (
        <CesPrices
          cesPrice={cesPrice}
          getUnitPrice={getUnitPrice}
          getPrice={getPrice}
          product={product}
          regionCode={regionCode}
          proPrice={proPrice}
        />
      ) : (
        <>
          {priceList && (
            <ol className={cx(`ol-${layout} ${priceType[page]} fa--prices`, isNewPriceApplicable && 'li-separation')}>
              {priceList.map((priceData, i) => (
                <Price
                  key={priceData.price?.[0]}
                  priceList={priceList}
                  productId={productId}
                  page={page}
                  isPumAvailable={isPumAvailable}
                  pumQuantity={pumQuantity}
                  pumUnit={pumUnit}
                  variant={variant}
                  labels={labels}
                  isConnectProduct={isConnectProduct}
                  operators={operators}
                  selectedOperatorId={selectedOperatorId}
                  appCtx={appCtx}
                  discountBadge={discountBadge}
                  priceCount={priceCount}
                  product={product}
                  userSegments={userSegments}
                  isUserLoggedIn={isUserLoggedIn}
                  {...priceData}
                  i={i}
                />
              ))}
            </ol>
          )}
          {showCesPrice && (
            <div className="ces-badge-container">
              <CesBadge
                campaignName={cesPrice?.campaignName}
                discountPercent={cesPrice?.metadata?.discountPercent}
                cesBadgeLabel={constants.CES_BADGE_LABEL[cesPrice?.campaignName]}
              />
            </div>
          )}
          {proPrice && !cmrPrice && (isSodimacSA || store === constants.STORES.sodimac) && !isWholesaleEnabled && (
            <ProPrice proPrice={proPrice} getPrice={getPrice} getUnitPrice={getUnitPrice} regionCode={regionCode} />
          )}
        </>
      )}

      {isWholesaleEnabled && proPrice && proPriceIsLower && (
        <div className="wholesale-container">
          <WholesaleBadge
            quantityToBuyValue={proPrice?.metadata?.quantityToBuyValue}
            quantityToBuyOperator={proPrice.campaignName === constants.PRICE_TYPE.PRO ? '>=' : '='}
            discountPercent={Number(proPrice?.metadata?.discountPercent)}
          />
          <ol className={cx(`ol-${layout} ${priceType[page]} fa--prices`, isNewPriceApplicable && 'li-separation')}>
            <Price
              priceList={priceList}
              productId={productId}
              page={page}
              isPumAvailable={isPumAvailable}
              pumQuantity={pumQuantity}
              pumUnit={pumUnit}
              variant={variant}
              labels={labels}
              isConnectProduct={isConnectProduct}
              operators={operators}
              selectedOperatorId={selectedOperatorId}
              appCtx={appCtx}
              priceCount={priceCount}
              product={product}
              userSegments={userSegments}
              isUserLoggedIn={isUserLoggedIn}
              {...proPrice.prices[0]}
              i={0}
            />
          </ol>
        </div>
      )}

      <style jsx>{pricesStyles}</style>
    </div>
  );
};

Prices.defaultProps = {
  productId: '',
  variantId: '',
  offeringId: '',
  isTabApplicable: false,
  page: 'POD',
  layout: '4_GRID',
  isPumAvailable: false,
  pumQuantity: 1,
  variant: 'PDP_MAIN',
  pumUnit: '',
  labels: {},
  operators: [],
  selectedOperatorId: undefined,
  isConnectProduct: false,
  appCtx: {},
  discountBadge: undefined,
  priceCount: undefined,
  userSegments: [],
  isUserLoggedIn: false,
  isVariant: false,
};

Prices.propTypes = {
  priceList: PropTypes.arrayOf(PropTypes.object).isRequired,
  productId: PropTypes.string,
  variantId: PropTypes.string,
  offeringId: PropTypes.string,
  isTabApplicable: PropTypes.bool,
  page: PropTypes.oneOf(['POD', 'PDP', 'CART', 'SLP', 'PLANS']),
  layout: PropTypes.string,
  isPumAvailable: PropTypes.bool,
  pumQuantity: PropTypes.number,
  variant: PropTypes.string,
  pumUnit: PropTypes.string,
  labels: PropTypes.object,
  isConnectProduct: PropTypes.bool,
  operators: PropTypes.arrayOf(PropTypes.object),
  selectedOperatorId: PropTypes.string,
  appCtx: PropTypes.object,
  discountBadge: PropTypes.object,
  priceCount: PropTypes.number,
  product: PropTypes.object.isRequired,
  userSegments: PropTypes.array,
  isUserLoggedIn: PropTypes.bool,
  isVariant: PropTypes.bool,
};

export default withApplicationContext(withUser(Prices));
export { Prices };
